import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import useAlertStore from '@/stores/alertStore';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useOrganizations } from '../hooks/useOrganizations';
import { useOrganizationFolders } from '../hooks/useOrganizationFolders';
import { reconcileNoteFolder } from '../utils/folderMembership';
import FolderShareModal from './FolderShareModal';
import type { Organization, OrganizationFolder } from '../types';

interface TeamShareFolderModalProps {
  open: boolean;
  onClose: () => void;
  organization: Organization;
  noteId: string;
}

/**
 * Folder picker for moving a note into the user's team, opened from the
 * "Move note" icon in the note header. Lists the folders the current user
 * can access (the API only returns accessible folders), defaults to General, and
 * lets them create a new folder inline. When the note is already in the team, the
 * same dialog moves it between folders or removes it.
 */
const TeamShareFolderModal = ({ open, onClose, organization, noteId }: TeamShareFolderModalProps) => {
  const showAlert = useAlertStore((state) => state.showAlert);

  const { addNote, removeNote } = useOrganizations();
  const { folders, addNoteToFolder, removeNoteFromFolder, refetch } = useOrganizationFolders(organization.id, open);

  const [selectedFolderId, setSelectedFolderId] = useState<string>('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);

  const isAlreadyInTeam = organization.note_ids.includes(noteId);
  const currentFolder = folders.find((f) => f.note_ids.includes(noteId));

  // Default the picker to the note's current folder (or General). Re-sync when
  // the dialog opens or the note's folder membership changes.
  useEffect(() => {
    if (!open) return;
    setSelectedFolderId(currentFolder?.id ?? 'general');
  }, [open, currentFolder?.id]);

  const handleShare = async () => {
    setIsSubmitting(true);
    try {
      await addNote({ organizationId: organization.id, noteId });
      if (selectedFolderId !== 'general') {
        try {
          await addNoteToFolder({ organizationId: organization.id, folderId: selectedFolderId, noteId });
        } catch (folderError: unknown) {
          // Roll back: the note was added to the team but folder filing failed —
          // remove it so it isn't silently left in General outside the chosen folder.
          await removeNote({ organizationId: organization.id, noteId }).catch(() => null);
          // Rethrow the ORIGINAL error. A synthetic `new Error('folder')` carries
          // no `detail`, so getApiErrorMessage below always fell through to the
          // generic message and the backend's real explanation was lost.
          throw folderError;
        }
      }
      showAlert(`Moved to ${organization.name}`, 'success');
      onClose();
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Something went wrong while moving to team. Try again later.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateFolder = async () => {
    setIsSubmitting(true);
    try {
      await reconcileNoteFolder(folders, organization.id, noteId, selectedFolderId, {
        addNoteToFolder,
        removeNoteFromFolder,
      });
      showAlert(selectedFolderId === 'general' ? 'Moved to General.' : 'Folder updated.', 'success');
      onClose();
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Something went wrong while updating the folder. Try again later.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnshare = async () => {
    setIsSubmitting(true);
    try {
      await removeNote({ organizationId: organization.id, noteId });
      // Best-effort: clear folder membership so stale ids can't reappear on re-share.
      const foldersWithNote = folders.filter((f) => f.note_ids.includes(noteId));
      await Promise.allSettled(
        foldersWithNote.map((folder) =>
          removeNoteFromFolder({ organizationId: organization.id, folderId: folder.id, noteId }),
        ),
      );
      showAlert(`Removed from ${organization.name}`, 'success');
      onClose();
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Something went wrong while removing from team. Try again later.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // The created folder is returned so we can refresh the list and pre-select it.
  const handleFolderCreated = async (folder?: OrganizationFolder) => {
    setCreateFolderOpen(false);
    await refetch();
    if (folder) setSelectedFolderId(folder.id);
  };

  return (
    <>
      <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{isAlreadyInTeam ? 'Move Note' : 'Move to team'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Choose Destination
            </Typography>

            <FormControl fullWidth>
              <InputLabel id="team-share-folder-label">Folder</InputLabel>
              <Select
                labelId="team-share-folder-label"
                label="Folder"
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
                disabled={isSubmitting}
              >
                <MenuItem value="general">General</MenuItem>
                {folders.map((folder) => (
                  <MenuItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="text"
              size="small"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={() => setCreateFolderOpen(true)}
              disabled={isSubmitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              New folder
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: isAlreadyInTeam ? 'space-between' : 'flex-end', px: 3, pb: 2 }}>
          {isAlreadyInTeam && (
            <Button color="error" onClick={handleUnshare} disabled={isSubmitting}>
              Remove from team
            </Button>
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={isAlreadyInTeam ? handleUpdateFolder : handleShare}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
            >
              {isAlreadyInTeam ? 'Save' : 'Move'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <FolderShareModal
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        organizationId={organization.id}
        memberEmails={organization.member_emails}
        ownerEmail={organization.owner_email}
        onSaved={handleFolderCreated}
      />
    </>
  );
};

export default TeamShareFolderModal;
